import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  FileText, 
  Star, 
  Mail, 
  TrendingUp,
  Users,
  CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/App';

const AdminDashboard = () => {
  const { admin } = useAuth();
  const [stats, setStats] = useState({
    locations: 0,
    articles: 0,
    testimonials: 0,
    contacts: 0,
    newContacts: 0
  });
  const [recentContacts, setRecentContacts] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${API}/locations`),
      axios.get(`${API}/blog?published_only=false`),
      axios.get(`${API}/testimonials?published_only=false`),
      axios.get(`${API}/contact`, { headers })
    ]).then(([locRes, blogRes, testRes, contactRes]) => {
      setStats({
        locations: locRes.data.length,
        articles: blogRes.data.length,
        testimonials: testRes.data.length,
        contacts: contactRes.data.length,
        newContacts: contactRes.data.filter(c => c.status === 'new').length
      });
      setRecentContacts(contactRes.data.slice(0, 5));
    }).catch(() => {});
  }, []);

  const statCards = [
    { icon: MapPin, label: 'Standorte', value: stats.locations, color: 'blue', link: '/admin/standorte' },
    { icon: FileText, label: 'Blog-Artikel', value: stats.articles, color: 'green', link: '/admin/blog' },
    { icon: Star, label: 'Bewertungen', value: stats.testimonials, color: 'yellow', link: '/admin/bewertungen' },
    { icon: Mail, label: 'Anfragen', value: stats.contacts, badge: stats.newContacts, color: 'purple', link: '/admin/anfragen' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">
          Willkommen zurück, {admin?.name || 'Admin'}!
        </h1>
        <p className="text-slate-600">Hier ist ein Überblick über deine Fahrschul-Website.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.link}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${stat.color}-100`}>
                    <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                  </div>
                </div>
                {stat.badge > 0 && (
                  <div className="mt-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {stat.badge} neu
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Contacts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Neueste Anfragen
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentContacts.length > 0 ? (
            <div className="space-y-4">
              {recentContacts.map((contact) => (
                <div key={contact.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">{contact.name}</p>
                    <p className="text-sm text-slate-600">{contact.email} • {contact.phone}</p>
                    <p className="text-sm text-slate-500">Klasse {contact.license_type} • {contact.location || 'Kein Standort'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`badge ${contact.status === 'new' ? 'badge-info' : contact.status === 'contacted' ? 'badge-warning' : 'badge-success'}`}>
                      {contact.status === 'new' ? 'Neu' : contact.status === 'contacted' ? 'Kontaktiert' : 'Erledigt'}
                    </span>
                    <span className="text-sm text-slate-400">
                      {new Date(contact.created_at).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Keine Anfragen vorhanden.</p>
          )}
          
          <Link 
            to="/admin/anfragen" 
            className="block text-center text-blue-600 hover:text-blue-700 font-medium mt-4"
          >
            Alle Anfragen ansehen →
          </Link>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <Link to="/admin/blog">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 text-center">
              <FileText className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">Neuen Artikel erstellen</h3>
              <p className="text-sm text-slate-600">Füge einen neuen Blog-Artikel hinzu</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/bewertungen">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 text-center">
              <Star className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">Bewertung hinzufügen</h3>
              <p className="text-sm text-slate-600">Neue Kundenbewertung eintragen</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/einstellungen">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">Einstellungen</h3>
              <p className="text-sm text-slate-600">Website-Einstellungen anpassen</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
